import moment from "moment";
import React, { Dispatch as ReactDispatch, SetStateAction } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { ITabReport } from "redux/app/logs/@types";
import { IStore } from "redux/store";

// Helper function to get validation status for a report item
const getValidationStatus = (item: any, isItADerivedReport?: boolean) => {
	// Embedded cargo reports don't have isValid flag and should not be restricted
	if (isItADerivedReport && item?.report_type === "CARGO") {
		return { isValid: "exempt", icon: "➖", text: "Not Applicable" };
	}
	
	if (item?.isValid === true) {
		return { isValid: true, icon: "✅", text: "Valid" };
	} else if (item?.isValid === false) {
		return { isValid: false, icon: "❌", text: "Invalid" };
	} else {
		return { isValid: undefined, icon: "⚠️", text: "Not Set" };
	}
};

const getImosReportName = (reportType: string) => {
	switch (reportType) {
		case "STEAMING":
			return "Noon Report At Sea";
		case "PORT":
			return "Noon Report In Port";
		case "ARRIVAL":
			return "Arrival Notice";
		case "DEPARTURE":
			return "Departure Notice";

		default:
			return reportType;
	}
};

// reports signifies timeline. Ensures that we show "➖" rather than sand-clock
function shouldShowDash(reports: any, index: number) {
	const currentReport = reports[index];
	const reportType = currentReport.report_type;

	// Check if there are more reports after the current one
	if (index < reports.length - 1) {
		// Case 1: Steaming followed by Arrival -> show dash in Steaming
		if (reportType === "STEAMING") {
			const nextReport = reports[index + 1];
			if (nextReport.report_type === "ARRIVAL") {
				return true;
			}
		}

		// Case 2: Port + Cargo + Departure -> show dash in Port & Cargo
		if (reportType === "PORT" && index < reports.length - 2) {
			const nextReport = reports[index + 1];
			const nextNextReport = reports[index + 2];
			if (
				nextReport.report_type === "CARGO" &&
				nextNextReport.report_type === "DEPARTURE"
			) {
				return true;
			}
		}

		if (reportType === "CARGO" && index > 0 && index < reports.length - 1) {
			const prevReport = reports[index - 1];
			const nextReport = reports[index + 1];
			if (
				prevReport.report_type === "PORT" &&
				nextReport.report_type === "DEPARTURE"
			) {
				return true;
			}
		}

		// Case 3: Port + Departure -> show dash in Port
		if (reportType === "PORT") {
			const nextReport = reports[index + 1];
			if (nextReport.report_type === "DEPARTURE") {
				return true;
			}
		}

		if(reportType === "BUNKER"){
			return true;
		}
	}

	return false;
}

type OwnProps = {
	item: any;
	index: string | number;
	isUserThirdParty: boolean;
	report: ITabReport;
	setErrorDetails: ReactDispatch<SetStateAction<any>>;
	setErrorDetailsModalOpen: ReactDispatch<SetStateAction<boolean>>;
	isItADerivedReport?: boolean;
	derivedReportHint?: string;
	timeline?: any[];
};
type ReportTimelineRowProps = PropsFromRedux & OwnProps;
function ReportTimelineRow(props: ReportTimelineRowProps) {
	const {
		index,
		item,
		isUserThirdParty,
		report,
		isItADerivedReport,
		derivedReportHint,
		timeline,
	} = props;
	const isImosDeferred = report?.imosApiStatus === "DEFERRED";
	const deferredImosDetails = {
		formID: null,
		success: false,
		formStatus: "DEFERRED",
		formErrors: null,
		message: "Deferred during SG/London office peak hours",
	};


	return (
		<tr className="h-full" key={index}>
			<td className="border border-gray-200 py-1 px-1 text-[6px] md:text-xs lg:text-[12px]  xl:text-sm lg:w-[86px] xl:w-[90px]">
				{isItADerivedReport ? <span title={derivedReportHint}>🔗 </span> : null}{" "}
				{item?.report_type}
			</td>
			<td className=" border border-gray-200 py-1 px-1 text-[6px] md:text-xs lg:text-[12px]  xl:text-sm lg:w-[100px] xl:w-[230px]">
				{getImosReportName(item.report_type)}
			</td>
			<td className=" text-gray-500 lg:text-[12px]  xl:text-sm border border-gray-200 py-1 px-1 text-[6px] md:text-xs">
				{item?.to_local_date} {item?.to_local_time} {item?.to_zd}
			</td>
			{!isUserThirdParty && (
				<td
					className="text-center border lg:text-[12px] w-8  xl:text-sm border-gray-200 py-1 px-1 text-[8px] md:text-xs "
					title={`${
						report?.signalApiLastProcessedDate
							? `Last Processed at ${moment(
									report?.signalApiLastProcessedDate
							  ).format("DD-MM-YYYY hh:mm a")}`
							: ""
					}`}
					onClick={() => {
						// find the erroneous report in the report.timeline by searching for the signalApiResponse
						// and show the response in a dialog/modal
						const erroneousReport = item;

						if (erroneousReport) {
							props.setErrorDetails({
								reportName: erroneousReport?.report_type,
								errorDetails: {
									formID: item?.signalApiResponse?.id,
									message: item?.signalApiResponse?.detail
										? item?.signalApiResponse?.detail
										: JSON.stringify(item?.signalApiResponse),

									success: item?.signalApiResponse?.id
										? true
										: item?.signalApiResponse?.detail
										? false
										: undefined,
								},
								lastProcessedDate: report?.signalApiLastProcessedDate,
								reportType: "SIGNAL",
								docId: report._id,
								vesselId: report.vesselId,
								doc_status: report.doc_status,
							});
							props.setErrorDetailsModalOpen(true);
						} else {
							console.log("Signal API Response not found in the report");
						}
					}}
				>
					{item?.signalApiResponse
						? item?.signalApiResponse?.id || item?.signalApiResponse === 200
							? "✅"
							: "❌"
						: timeline &&
						  typeof index === "number" &&
						  shouldShowDash(timeline, index)
						? "➖"
						: "⏳"}
				</td>
			)}
			{!isUserThirdParty && (
				<td
					className="text-center cursor-pointer  lg:text-[12px] w-8  xl:text-sm border border-gray-200 py-1 px-1 text-[8px] md:text-xs"
					title={`${
						item?.report_type === "CARGO"
							? "Not Applicable, will be sent with departure notice, if any"
							: isImosDeferred
							? "Deferred during SG/London office peak hours"
							: report?.imosApiLastProcessedDate
							? `Last Processed at ${moment(
									report?.imosApiLastProcessedDate
							  ).format("DD-MM-YYYY hh:mm a")}`
							: ""
					}`}
					// on click of IMOS status, it should show the IMOS API response in a dialog/modal
					onClick={() => {
						// find the erroneous report in the report.timeline by searching for the imosApiResponse
						// and show the response in a dialog/modal
						const erroneousReport = item;

						if (erroneousReport) {
							props.setErrorDetails({
								reportName: erroneousReport?.report_type,
								errorDetails:
									erroneousReport?.imosApiResponse ||
									(isImosDeferred ? deferredImosDetails : undefined),
								lastProcessedDate: report?.imosApiLastProcessedDate,
								reportType: "IMOS",
								docId: report._id,
								vesselId: report.vesselId,
								doc_status: report.doc_status,
							});
							props.setErrorDetailsModalOpen(true);
						} else {
							console.log("IMOS API Response not found in the report");
						}
					}}
				>
					{/* if report type is CARGO then the status is not applicable to it, just show ➖ */}
					{item?.report_type === "CARGO"
						? "➖"
						: item?.imosApiResponse
						? item?.imosApiResponse?.success
							? "✅"
							: "❌"
						: isImosDeferred
						? "⏸️"
						: timeline &&
						  typeof index === "number" &&
						  shouldShowDash(timeline, index)
						? "➖"
						: "⏳"}
				</td>
			)}
			{/* Validation Status Column */}
			<td
				className="text-center border lg:text-[12px] w-8  xl:text-sm border-gray-200 py-1 px-1 text-[8px] md:text-xs"
				title={`Validation Status: ${getValidationStatus(item, isItADerivedReport).text}`}
			>
				{getValidationStatus(item, isItADerivedReport).icon}
			</td>
		</tr>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(ReportTimelineRow);
